import axios from 'axios';
import {
	ApiError,
	ApiOriginalError,
	CloseContractRequest,
	CloseContractResponse,
	EmailContractRequest,
	EmailContractResponse,
	GDPR,
	GDPRRequest,
	IBANCheck,
	IBANCheckRequest,
	Precontractual,
	PrecontractualRequest,
	Profile,
	RefreshSignature,
	RefreshSignatureRequest,
	Tariffing, TariffingEpRequest,
	TariffingRequest,
	ThemeCustomization,
} from '../../../types/api';

import {AppStore, Constalation, FahrzeugType, Produkt, SalesType} from '../../../types/redux';
import { log } from '../../../utils/logger';
import { pick } from '../../utils';
import { apiConfig } from './auth';
import { toEnumKey } from "../../../utils/enum";

const apiError = (error: any): ApiError => {
	if (error.response && error.response.data) {
		const { code, beschreibung } = error.response.data as ApiOriginalError;

		return { code, error: beschreibung };
	}

	return { code: '', error: 'Undefined error' };
};

export const fetchUserProfile = (store: AppStore): Promise<Profile> =>
	axios
		.get('/api/benutzer/v3', apiConfig(store))
		.then((res) => res.data)
		.catch((error) => {
			log('ERROR', error, error.response);
			throw error;
		});

const testEmailLogin = "uitest@tester.de";
const testProzessId = "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx";

export const validateIBAN = (
	store: AppStore,
	iban: string
): Promise<IBANCheck> => {
	const params: IBANCheckRequest = { iban };

	const { user } = store.getState();
	if (user.email === testEmailLogin ) {
		const ic : IBANCheck = {iban:"DE67500700100093008102", bic: ["DEUTDEFFXXX"], bankname:"Deutsche Bank"}
		return new Promise<IBANCheck>(resolve => resolve(ic));
	}

	return axios
		.get('/api/bank/ibanPruefung/v1', { ...apiConfig(store), params })
		.then((res) => res.data)
		.catch((error) => error.data);
};

export const refreshSignature = (
	store: AppStore
): Promise<RefreshSignature> => {
	// Setup payload data from redux
	const keys = ['prozessId'];
	const { contract } = store.getState();
	const params: RefreshSignatureRequest = pick(contract, keys);

	return axios
		.post('/api/unterschriftWiederholen/v1', params, apiConfig(store))
		.then((res) => res.data)
		.catch((error) => {
			log('ERROR', error, error.response);
			throw error;
		});
};

// IMPORTANT: The following APIs must be called in sequence, with the same prozessId, and each API call
// must be done only once. Otherwise, the API server wil return a 404 error (P1: Ungueltige ProzessId).

export const fetchTariffingKpp = (
	store: AppStore,
	months: number,
	beginDateOverride?: string
): Promise<Tariffing> => {
	// Setup payload data from redux
	const keys = ['kaufdatum', 'beginndatum', 'neufahrzeug'];
	const { contract, user } = store.getState();
	const params: TariffingRequest = pick(contract, keys);
	for (let fahrzeugTypeKey in FahrzeugType)
		if (FahrzeugType[fahrzeugTypeKey] === contract.fahrzeugtyp)
			params.art = fahrzeugTypeKey;
	params.laufzeit = months;
	if (beginDateOverride) {
		params.beginndatum = beginDateOverride;
	}

	if (user.email === testEmailLogin) {
		let ct: Tariffing  = { prozessId: testProzessId, beitrag: 199.0 };
		switch (months) {
			case 36: ct = { prozessId: testProzessId, beitrag: 299.0 }; break;
			case 48: ct = { prozessId: testProzessId, beitrag: 399.0 }; break;
			case 60: ct = { prozessId: testProzessId, beitrag: 499.0 }; break;
		}

		return new Promise<Tariffing>(resolve => resolve(ct));
	}
	return axios // TODO refactor
		.post(`/api/tarifierung/${contract.salesType}/v1`, params, apiConfig(store))
		.then((res) => res.data)
		.catch((error) => {
			throw apiError(error);
		});
};

// 2. Sends privacy information to the user's e-mail
export const sendGDPR = (store: AppStore): Promise<GDPR> => {
	// Setup payload data from redux
	const keys = ['prozessId', 'email', 'kaufvertragsnummer', 'telefonnummer'];
	const { contract, user } = store.getState();
	const params: GDPRRequest = pick(contract, keys);

	if (user.email === testEmailLogin) {
		return new Promise<GDPR>(resolve => resolve(''));
	}

	return axios
		.post('/api/datenschutzInfomationen/v2', params, apiConfig(store))
		.then((res) => res.data)
		.catch((error) => {
			throw apiError(error);
		});
};

// 3. Send pre-contractual data
export const sendPrecontractual = (
	store: AppStore
): Promise<Precontractual> => {
	// Setup payload data from redux
	const keys = [
		'ausweisId',
		'email',
		'geburtsdatum',
		'iban',
		'name',
		'ort',
		'partnerId',
		'partnerNumber',
		'plz',
		'prozessId',
		'strasse',
		'vorname',
	];
	const { contract, user } = store.getState();
	const params: PrecontractualRequest = pick(contract, keys);

	if (user.email === testEmailLogin) {
		const pc: Precontractual =  { launchUrl:"null", launchUrl2:"null", businesscaseId:"null"};
		return new Promise<Precontractual>(resolve => resolve(pc));
	}
	return axios
		.post('/api/vorvertraglicheInfomationen/v1', params, apiConfig(store))
		.then((res) => res.data)
		.catch((error) => {
			throw apiError(error);
		});
};

// 4a. Binding contract
export const sendCloseContract = (
	store: AppStore
): Promise<CloseContractResponse> => {
	// Setup payload data from redux
	const keys = ['prozessId'];
	const { contract, user } = store.getState();
	const params: CloseContractRequest = pick(contract, keys);

	if (user.email === testEmailLogin) {
		const ccr :CloseContractResponse= {platzhalter: "null"};
		return new Promise<CloseContractResponse>(resolve => resolve(ccr));
	}
	return axios
		.post('/api/vertrag/v1', params, apiConfig(store))
		.then((res) => res.data)
		.catch((error) => {
			throw apiError(error);
		});
};

// 4b. Email contract for signature at a later point in time
export const sendEmailContract = (
	store: AppStore
): Promise<EmailContractResponse> => {
	// Setup payload data from redux
	const keys = ['prozessId'];
	const { contract, user } = store.getState();
	const params: EmailContractRequest = pick(contract, keys);

	if (user.email === testEmailLogin) {
		const ecr :EmailContractResponse= {uuid: "null"};
		return new Promise<EmailContractResponse>(resolve => resolve(ecr));
	}
	return axios
		.post('/api/antragMailen/v1', params, apiConfig(store))
		.then((res) => res.data)
		.catch((error) => {
			throw apiError(error);
		});
};

export const getNotifications = (): Promise<any> => {
	const appVersion = 0;
	const appPackage = 'de.orbitdigital';

	return axios
		.get(`/api/version/v2?appVersion=${appVersion}&appPackage=${appPackage}`)
		.then((res) => res.data)
		.catch((error) => {
			throw apiError(error);
		});
};

export const fetchCustomizing = (
	customizingId: string
): Promise<ThemeCustomization | null> =>
	axios
		.get(`/api/customizing/v1?id=${customizingId}`)
		.then((res) => res.data as ThemeCustomization)
		.catch((error) => {
			throw apiError(error);
		});


export const fetchTariffingEpe = (
	store: AppStore,
	produkt?: Constalation,
	beginDateOverride?: string
): Promise<Tariffing> => {
	const keys = [
		'neufahrzeug', 'kaufdatum',
		'kaufpreis', 'zahlweise',
		'laufzeit', 'beginndatum'
	];
	const { contract, user } = store.getState();
	const params: TariffingEpRequest = pick(contract, keys);
	params.art = toEnumKey(FahrzeugType, contract.fahrzeugtyp as FahrzeugType) as unknown as FahrzeugType;
	params.produkt = toEnumKey(Produkt, contract.produkt as Produkt) as unknown as Produkt;
	params.salesType = toEnumKey(SalesType, contract.salesType as SalesType) as unknown as SalesType;
	if (beginDateOverride) {
		params.beginndatum = beginDateOverride;
	}
	if (user.email === testEmailLogin) {
		let ct: Tariffing = {prozessId: testProzessId, beitrag: 123.0};
		return new Promise<Tariffing>(resolve => resolve(ct));
	}
	const apiPath = `/api/tarifierung/ep/v1`
	return axios
		.post(apiPath, params, apiConfig(store))
		.then((res) => res.data)
		.catch((error) => {
			throw apiError(error);
		});
};
